// Agent Analytics Component
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';


const AgentAnalytics = ({ user }) => {
  const [agentStats, setAgentStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAgentAnalytics();
  }, []);

  const loadAgentAnalytics = async () => {
    // Mock agent analytics data
    const mockStats = {
      totalCalls: 1247,
      successRate: 98.5,
      avgResponseTime: 1.8,
      agents: [
        {
          name: 'WeatherAgent',
          calls: 423,
          successRate: 99.1,
          avgResponseTime: 1.2,
          status: 'active',
          lastUsed: new Date(Date.now() - 1000 * 60 * 30).toISOString()
        },
        {
          name: 'CalculatorAgent',
          calls: 312,
          successRate: 98.7,
          avgResponseTime: 2.1,
          status: 'active',
          lastUsed: new Date(Date.now() - 1000 * 60 * 45).toISOString()
        },
        {
          name: 'FileAgent',
          calls: 289,
          successRate: 97.9,
          avgResponseTime: 2.5,
          status: 'active',
          lastUsed: new Date(Date.now() - 1000 * 60 * 60).toISOString()
        },
        {
          name: 'NewsAgent',
          calls: 156,
          successRate: 96.8,
          avgResponseTime: 3.2,
          status: 'warning',
          lastUsed: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString()
        },
        {
          name: 'ReminderAgent',
          calls: 67,
          successRate: 100,
          avgResponseTime: 0.8,
          status: 'active',
          lastUsed: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString()
        }
      ]
    };
    setAgentStats(mockStats);
    setLoading(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'warning': return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      default: return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Agent Analytics</h2>
          <p className="text-gray-600">Monitor agent performance and usage patterns</p>
        </div>
        <Link to="/agents" className="btn-primary">
          Manage Agents
        </Link>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-2xl font-bold text-blue-600">{agentStats.totalCalls}</div>
          <div className="text-sm text-gray-600">Total Agent Calls</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-2xl font-bold text-green-600">{agentStats.successRate}%</div>
          <div className="text-sm text-gray-600">Success Rate</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-2xl font-bold text-purple-600">{agentStats.avgResponseTime}s</div>
          <div className="text-sm text-gray-600">Avg Response Time</div>
        </div>
      </div>

      {/* Agent Performance */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Agent Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Calls
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Success Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Used
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {agentStats.agents?.map((agent, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <CpuChipIcon className="w-5 h-5 text-purple-500 mr-3" />
                      <span className="text-sm font-medium text-gray-900">{agent.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.calls}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.successRate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.avgResponseTime}s
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(agent.status)}
                      <span className="ml-2 text-sm text-gray-900 capitalize">{agent.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(agent.lastUsed).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgentAnalytics;